import { API_BASE_URL_ADMIN } from 'shared/config';
import { API_URL } from 'shared/constants/api-url';
import { httpClient } from 'shared/services/http-client';

export type ContentStatus = 'draft' | 'published';

export type CourseArchiveFilter = 'active' | 'archived' | 'all';

export type ContentVideoItem = {
  id: string;
  folderId: string;
  sortOrder: number;
  src: string;
  name: string;
  description: string | null;
  status: ContentStatus;
  files?: ContentFile[];
  needUserFeedback?: boolean;
  feedbackRequiredToProgress?: boolean;
  createdAt: string;
  updatedAt: string | null;
};

export type ContentFile = {
  name: string;
  url: string;
};

export type VideoFormFile = {
  id: string;
  name: string;
  url?: string;
  file?: File;
};

export type ContentFolderItem = {
  id: string;
  courseId: string;
  name: string;
  sortOrder: number;
  isSequentialLock: boolean;
  status: ContentStatus;
  createdAt: string;
  updatedAt: string | null;
  videos: ContentVideoItem[];
};

export type CuratorItem = {
  id: string;
  name: string;
  email?: string;
  role?: string;
};

export type ContentCourseItem = {
  id: string;
  name: string;
  description: string | null;
  sortOrder: number;
  audience: string | null;
  status: ContentStatus;
  isForAllCompanies: boolean;
  companyIds: string[];
  curatorIds?: string[];
  curators?: CuratorItem[];
  foldersCount?: number;
  videosCount?: number;
  createdAt: string;
  updatedAt: string | null;
  deletedAt: string | null;
};

export type ContentCourseDetail = ContentCourseItem & {
  folders: ContentFolderItem[];
};

export type ContentCoursesResponse = {
  data: ContentCourseItem[];
  count: number;
  total: number;
  page: number;
  totalPage: number;
};

export type ContentFoldersResponse = {
  data: ContentFolderItem[];
  count: number;
  total: number;
  page: number;
  totalPage: number;
};

export type CompanyItem = {
  id: string;
  name: string;
};

export type CompaniesResponse = {
  data: CompanyItem[];
  count: number;
  total: number;
  page: number;
  totalPage: number;
};

export type CourseForm = {
  id?: string;
  name: string;
  description: string;
  audience: string;
  status: ContentStatus;
  isForAllCompanies: boolean;
  companyIds: string[];
  curatorIds: string[];
};

export type FolderForm = {
  id?: string;
  courseId: string;
  name: string;
  isSequentialLock: boolean;
  status: ContentStatus;
};

export type VideoForm = {
  id?: string;
  folderId: string;
  src: string;
  name: string;
  description: string;
  status: ContentStatus;
  files: VideoFormFile[];
  needUserFeedback: boolean;
  feedbackRequiredToProgress: boolean;
};

export type ReorderItem = {
  id: string;
  sortOrder: number;
};

const PAGE_SIZE = 50;

export const loadCourses = async (
  page = 1,
  archiveFilter: CourseArchiveFilter = 'active',
) => {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(PAGE_SIZE));
  params.set('archiveFilter', archiveFilter);

  const response = await httpClient(
    `${API_BASE_URL_ADMIN}${API_URL.contentCourses}?${params.toString()}`,
  );

  return response.json as ContentCoursesResponse;
};

export const loadCourseById = async (courseId: string) => {
  const response = await httpClient(
    `${API_BASE_URL_ADMIN}${API_URL.contentCourse(courseId)}`,
  );

  return response.json as ContentCourseDetail;
};

export const loadFolders = async (courseId: string, page = 1) => {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(PAGE_SIZE));
  params.set('courseId', courseId);

  const response = await httpClient(
    `${API_BASE_URL_ADMIN}${API_URL.contentFolders}?${params.toString()}`,
  );

  return response.json as ContentFoldersResponse;
};

export const loadCompanies = async () => {
  const response = await httpClient(
    `${API_BASE_URL_ADMIN}${API_URL.companies}?limit=1000&page=1`,
  );

  return response.json as CompaniesResponse;
};

export type CuratorsResponse = {
  data: CuratorItem[];
  count?: number;
  total?: number;
  page?: number;
};

export const loadCurators = async () => {
  const response = await httpClient(
    `${API_BASE_URL_ADMIN}${API_URL.admins}?role=curator&limit=1000&page=1`,
  );

  return response.json as CuratorsResponse;
};

export const saveCourse = async (payload: Record<string, unknown>) => {
  await httpClient(`${API_BASE_URL_ADMIN}${API_URL.contentCourses}`, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: new Headers({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }),
  });
};

export const archiveCourse = async (id: string) => {
  await httpClient(`${API_BASE_URL_ADMIN}${API_URL.contentCourse(id)}`, {
    method: 'DELETE',
    headers: new Headers({ Accept: 'application/json' }),
  });
};

export const restoreCourse = async (id: string) => {
  await httpClient(`${API_BASE_URL_ADMIN}${API_URL.contentCourseRestore(id)}`, {
    method: 'PATCH',
    headers: new Headers({ Accept: 'application/json' }),
  });
};

export const deleteCourse = archiveCourse;

export const reorderCourses = async (items: ReorderItem[]) => {
  await httpClient(`${API_BASE_URL_ADMIN}${API_URL.contentCoursesReorder}`, {
    method: 'PATCH',
    body: JSON.stringify({ items }),
    headers: new Headers({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }),
  });
};

export const saveFolder = async (payload: Record<string, unknown>) => {
  await httpClient(`${API_BASE_URL_ADMIN}${API_URL.contentFolders}`, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: new Headers({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }),
  });
};

export const deleteFolder = async (id: string) => {
  await httpClient(`${API_BASE_URL_ADMIN}${API_URL.contentFolder(id)}`, {
    method: 'DELETE',
    headers: new Headers({ Accept: 'application/json' }),
  });
};

export const reorderFolders = async (items: ReorderItem[]) => {
  await httpClient(`${API_BASE_URL_ADMIN}${API_URL.contentFoldersReorder}`, {
    method: 'PATCH',
    body: JSON.stringify({ items }),
    headers: new Headers({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }),
  });
};

export const saveVideo = async (payload: Record<string, unknown>) => {
  await httpClient(`${API_BASE_URL_ADMIN}${API_URL.content}`, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: new Headers({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }),
  });
};

export const uploadContentFile = async (file: File) => {
  const body = new FormData();
  body.append('file', file);

  const response = await httpClient(
    `${API_BASE_URL_ADMIN}${API_URL.contentUpload}`,
    {
      method: 'POST',
      body,
    },
  );
  const uploaded = response.json as { url?: string };

  if (!uploaded.url) throw new Error('Backend не вернул ссылку на файл');

  return uploaded.url;
};

export const deleteVideo = async (id: string) => {
  await httpClient(`${API_BASE_URL_ADMIN}${API_URL.contentItem(id)}`, {
    method: 'DELETE',
    headers: new Headers({ Accept: 'application/json' }),
  });
};

export const reorderVideos = async (folderId: string, items: ReorderItem[]) => {
  await httpClient(`${API_BASE_URL_ADMIN}${API_URL.contentReorder}`, {
    method: 'PATCH',
    body: JSON.stringify({ folderId, items }),
    headers: new Headers({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }),
  });
};

export const DEFAULT_COURSE_FORM: CourseForm = {
  name: '',
  description: '',
  audience: '',
  status: 'draft',
  isForAllCompanies: true,
  companyIds: [],
  curatorIds: [],
};

export const DEFAULT_FOLDER_FORM = (courseId: string): FolderForm => ({
  courseId,
  name: '',
  isSequentialLock: false,
  status: 'draft',
});

export const DEFAULT_VIDEO_FORM = (folderId: string): VideoForm => ({
  folderId,
  src: '',
  name: '',
  description: '',
  status: 'draft',
  files: [],
  needUserFeedback: false,
  feedbackRequiredToProgress: false,
});
